/**
 * Annexure Column Definitions and Validation Schema for New TDS Return Form 140 (replaces 26Q)
 * Applicable from FY 2026-27 onwards.
 *
 * Key changes from legacy 26Q annexure:
 * - Removed: Interest, Others, old Total, Deductee Reference No., Deductee code,
 *   Paid by book entry, old sub-clause cash withdrawal fields (4 fields)
 * - Renamed: "Section Under Which Payment Made" → "Section Code"
 * - Renamed: "Total TDS to be allocated" → "Total" (logic: Total Tax Deposited - Others from allocate)
 * - Added: "Unique Identification Number of Form No. 121" (Alphanumeric 26)
 * - Removed individual: TDS, Surcharge, Health and Education Cess (only Total tax deducted remains)
 * - Language updates on cash withdrawal fields (194N → 393(3)), certificate (u/s 197 → section 395)
 */

import { z } from 'zod'
import {
  mandatoryNumeric12_2,
  ddMMYYYYWithSlash,
  ddMMYYYYWithSlashOptional,
  isZeroOrEmptyNumeric,
} from './validationHelpers'

/**
 * Zod validation schema for Annexure rows in new Form 140.
 *
 * Differences from legacy returnsAnnexureRowSchema:
 * - Removed: tds, surcharge, healthAndEducationCess (individual fields gone)
 * - Removed: deducteeReferenceNo, deducteeCode (fields no longer exist in new form)
 * - Added: uniqueIdForm121 (Alphanumeric 26, optional)
 * - Retained: totalTaxDeducted (as mandatory field replacing individual breakdown)
 * - Retained: challanSerialNo, sectionCode, panOfDeductee, deducteeName,
 *             dateOfPaymentOrCredit, amountPaidOrCredited, totalTaxDeposited,
 *             rateOfTaxDeducted, dateOfDeduction, certificateNo197, remarks
 */
export const returnsAnnexureRowSchema140 = z
  .object({
    challanSerialNo: z
      .string()
      .nonempty({ message: 'Challan Serial No. is required.' })
      .regex(/^\d{1,9}$/, {
        message: 'Must be numeric and up to 9 digits.',
      }),

    sectionCode: z.string().nonempty({ message: 'Section Code is required.' }),

    panOfDeductee: z
      .string()
      .min(10, 'PAN is required')
      .max(10)
      .regex(
        /^[A-Z]{3}[ABCFGHLJPT]{1}[A-Z]{1}[0-9]{4}[A-Z]{1}$/i,
        'Invalid PAN format'
      ),

    deducteeName: z
      .string()
      .nonempty({ message: 'Deductee Name is required.' })
      .regex(/^[^^\u005E]*$/, 'Invalid characters (caret ^ not allowed)'),

    dateOfPaymentOrCredit: ddMMYYYYWithSlash,

    amountPaidOrCredited: mandatoryNumeric12_2,
    totalTaxDeposited: mandatoryNumeric12_2,
    totalTaxDeducted: mandatoryNumeric12_2,

    // Conditionally required: enforced via superRefine based on totalTaxDeducted.
    dateOfDeduction: ddMMYYYYWithSlashOptional,

    rateOfTaxDeducted: z.string().regex(/^\d{1,3}(\.\d{1,2})?$/, {
      message: 'Must be a number up to 3 digits and 2 decimals',
    }),

    certificateNo197: z
      .string()
      .regex(/^[^^\u005E]*$/, 'Invalid characters (caret ^ not allowed)')
      .nullable()
      .optional(),

    remarks: z
      .string()
      .optional()
      .nullable(),

    uniqueIdFormNo121: z
      .string()
      .max(26, { message: 'Must be at most 26 characters' })
      .regex(/^[A-Za-z0-9!@#$%&*()_+\-=[\]{}|;:',.<>/?~`]*$/, {
        message: 'Invalid characters (caret ^ not allowed)',
      })
      .nullable()
      .optional(),
  })
  .superRefine((data, ctx) => {
    // Date of deduction is required only when tax has actually been deducted.
    // When Total Tax Deducted is zero/undefined/null, it stays optional.
    if (!isZeroOrEmptyNumeric(data.totalTaxDeducted) && !data.dateOfDeduction) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['dateOfDeduction'],
        message: 'Date of Deduction is required.',
      })
    }
  })

export const RETURNS_ANNEXURE_COLUMNS_140 = [
  {
    id: 'serialNo',
    label: 'Row number',
    width: '150px',
  },
  {
    id: 'challanSerialNo',
    label: 'Challan serial no.',
    width: '150px',
    editable: true,
    isFormatted: false,
  },
  {
    id: 'updateMode',
    label: 'Update mode for deductee (Add/ Update/ PAN Update)',
    width: '250px',
    align: 'center',
  },
  {
    id: 'bsrCode',
    label: 'BSR code of branch where tax deposited',
    width: '250px',
    align: 'center',
    isFormatted: false,
  },
  {
    id: 'dateOfTaxDeposited',
    label: 'Date on which tax deposited (DD/MM/YYYY)',
    width: '250px',
    align: 'center',
  },
  {
    id: 'transferVoucherOrChallanSerialNo',
    label: 'Transfer voucher/ challan serial no.',
    width: '200px',
    align: 'center',
    isFormatted: false,
  },
  {
    id: 'totalTdsToBeAllocated',
    label: 'Total',
    width: '100px',
    align: 'center',
    tooltip: '(Total Tax Deposited - Others) from allocate page',
  },
  {
    id: 'deducteeSerialNo',
    label: 'S. no.',
    width: '150px',
  },
  {
    id: 'lastPanOfDeductee',
    label: 'Last PAN of deductee',
    width: '200px',
  },
  {
    id: 'panOfDeductee',
    label: 'PAN of the deductee',
    width: '160px',
    editable: true,
  },
  {
    id: 'deducteeName',
    label: 'Name of the deductee',
    width: '240px',
    editable: true,
  },
  {
    id: 'sectionCode',
    label: 'Section code',
    width: '200px',
    align: 'left',
    editable: true,
    isFormatted:false,
    type: 'dropdown',
    options: [], // Populated dynamically from NEW_SECTION_CODE API
  },
  {
    id: 'dateOfPaymentOrCredit',
    label: 'Date of payment/ credit (DD/MM/YYYY)',
    width: '250px',
    align: 'center',
    editable: true,
  },
  {
    id: 'amountPaidOrCredited',
    label: 'Amount paid or credited',
    width: '200px',
    align: 'center',
    editable: true,
  },
  {
    id: 'cashWithdrawalExceeding1Cr',
    label:
      'Amount of withdrawal exceeds Rs. 1 Crore as referred to in section 393(3) [Table: Sl. No. 5] (in case of person other than cooperative society)',
    width: '300px',
    align: 'center',
  },
  {
    id: 'cashWithdrawalExceeding3CrCooperative',
    label:
      'Amount of cash withdrawal where the aggregate amount of withdrawal exceeds Rs. 3 Crore for cases covered by section 393(3) [Table: Sl. No. 5] (in case of cooperative societies)',
    width: '300px',
    align: 'center',
  },
  {
    id: 'totalTaxDeducted',
    label: 'Total tax deducted',
    width: '160px',
    align: 'center',
  },
  {
    id: 'lastTotalTaxDeducted',
    label: 'Last total tax deducted',
    width: '180px',
    align: 'center',
  },
  {
    id: 'totalTaxDeposited',
    label: 'Total tax deposited',
    width: '160px',
    align: 'center',
    editable: true,
  },
  {
    id: 'lastTotalTaxDeposited',
    label: 'Last total tax deposited',
    width: '240px',
  },
  {
    id: 'rateOfTaxDeducted',
    label: 'Rate at which tax deducted',
    width: '160px',
    editable: true,
  },
  {
    id: 'dateOfDeduction',
    label: 'Date of deduction (DD/MM/YYYY)',
    width: '240px',
    align: 'center',
    editable: true,
  },
  {
    id: 'remarks',
    label:
      'Reason for non-deduction/ Lower deduction/ Higher deduction/ Threshold/ Transporter etc.',
    width: '350px',
    editable: true,
  },
  {
    id: 'certificateNo197',
    label:
      'Certificate number of certificate issued under section 395 for non-deduction/ lower deduction',
    width: '300px',
    align: 'center',
    editable: true,
  },
  {
    id: 'uniqueIdFormNo121',
    label:
      'Unique Identification Number of Form No. 121 submitted by the deductee',
    width: '300px',
    align: 'center',
    editable: true,
    maxLength: 26,
    tooltip: 'Alphanumeric (26 characters), special characters allowed',
  },
]
